'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import {
  fetchTodayWarehouseDispatches,
  receiveWarehouseDispatch,
  scanWarehouseDispatchQr,
  type ApiWarehouseDispatch,
  type ApiWarehouseDispatchLine,
} from '@/features/kitchen/api/warehouseDispatchApi'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import {
  isLineMatchPlan,
  qtyShortageOf,
  summarizeMatchCounts,
} from '@/features/kitchen/warehouseReceiveLines'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QrCameraScanner } from '@/shared/components/QrCameraScanner'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { getKitchenSession } from '@/shared/auth/sessionStorage'
import { canPerformHeadActions } from '@/shared/auth/canPerformHeadActions'

type View = 'list' | 'scan' | 'confirm'

type ActiveDispatch = {
  id: string
  code: string
  warehouseName: string
  delmanName: string
}

/** Baris qty terima per material. User mengedit diterima (baik) + rusak;
 * kurang = rencana − baik − rusak (invarian baik+rusak+kurang = planned). */
type ReceiveLineState = {
  materialId: string
  materialName: string
  uom: string
  qtyPlanned: number
  qtyGood: number
  qtyDamaged: number
}

const FLOW_STEPS = kitchenTerms.warehouseReceive.flowSteps.map((label) => ({ label }))

function flowStep(view: View): number {
  if (view === 'confirm') return 3
  if (view === 'scan') return 2
  return 1
}

function statusLabel(status: string): string {
  if (status === 'received') return kitchenTerms.warehouseReceive.status.received
  return kitchenTerms.warehouseReceive.status.inTransit
}

function statusBadge(status: string): string {
  if (status === 'received') return 'badge-success'
  return 'badge-warning'
}

function linesToReceiveState(lines: ApiWarehouseDispatchLine[]): ReceiveLineState[] {
  return lines.map((line) => {
    const damaged = line.qty_damaged ?? 0
    const shortage = line.qty_shortage ?? 0
    const goodApi = line.qty_good ?? 0
    const accounted = goodApi + damaged + shortage
    // Belum ada qty terima tersimpan → default semua diterima baik
    if (accounted < 1e-9) {
      return {
        materialId: line.material_id,
        materialName: line.material_name,
        uom: line.uom,
        qtyPlanned: line.qty_planned,
        qtyGood: line.qty_planned,
        qtyDamaged: 0,
      }
    }
    return {
      materialId: line.material_id,
      materialName: line.material_name,
      uom: line.uom,
      qtyPlanned: line.qty_planned,
      qtyGood: Math.min(line.qty_planned, Math.max(0, goodApi)),
      qtyDamaged: Math.min(line.qty_planned, Math.max(0, damaged)),
    }
  })
}

function summaryText(lines: ApiWarehouseDispatchLine[]): string {
  const total = lines.reduce((sum, line) => sum + line.qty_planned, 0)
  return kitchenTerms.warehouseReceive.summary.join([
    kitchenTerms.warehouseReceive.summary.materialCount(lines.length),
    `${total} qty`,
  ])
}

export function WarehouseReceivePage() {
  const device = getKitchenDevice()
  const session = getKitchenSession()
  const useApi = shouldUseKitchenApi()
  const kitchenName = device?.kitchenName ?? 'Dapur'
  const canPerformHeadActionsForSession = session ? canPerformHeadActions(session) : false

  const [view, setView] = useState<View>('list')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [incoming, setIncoming] = useState<ApiWarehouseDispatch[]>([])

  const [activeDispatch, setActiveDispatch] = useState<ActiveDispatch | null>(null)
  const [receiveLines, setReceiveLines] = useState<ReceiveLineState[]>([])
  const [expandedMaterialId, setExpandedMaterialId] = useState<string | null>(null)

  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [scanToken, setScanToken] = useState('')
  const [cameraActive, setCameraActive] = useState(true)
  const [isVerifyingScan, setIsVerifyingScan] = useState(false)
  const [manualScanOpen, setManualScanOpen] = useState(false)

  const todayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const reload = useCallback(async () => {
    if (!device?.deviceId || !canPerformHeadActionsForSession) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    try {
      const res = await fetchTodayWarehouseDispatches(device.deviceId)
      setIncoming(res.incoming)
    } catch {
      setLoadError(kitchenTerms.warehouseReceive.loadError)
    } finally {
      setLoading(false)
    }
  }, [device?.deviceId, canPerformHeadActionsForSession])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  useEffect(() => {
    if (!device?.deviceId || !canPerformHeadActionsForSession) return undefined
    const hasPending = incoming.some((item) => item.status !== 'received')
    if (!hasPending) return undefined
    const timer = window.setInterval(() => void reload(), 5_000)
    return () => window.clearInterval(timer)
  }, [device?.deviceId, canPerformHeadActionsForSession, incoming, reload])

  useEffect(() => {
    if (view === 'scan') {
      setCameraActive(true)
      setIsVerifyingScan(false)
    }
  }, [view])

  const pendingIncoming = useMemo(() => incoming.filter((item) => item.status !== 'received'), [incoming])
  const doneIncoming = useMemo(() => incoming.filter((item) => item.status === 'received'), [incoming])

  const isBalanced = useMemo(
    () =>
      receiveLines.every(
        (line) => Math.abs(line.qtyGood + line.qtyDamaged + qtyShortageOf(line) - line.qtyPlanned) < 1e-9,
      ),
    [receiveLines],
  )

  const matchCounts = useMemo(() => summarizeMatchCounts(receiveLines), [receiveLines])

  function openConfirmFromDispatch(item: ApiWarehouseDispatch) {
    setActiveDispatch({
      id: item.id,
      code: item.code,
      warehouseName: item.warehouse_name,
      delmanName: item.delman_name,
    })
    setReceiveLines(linesToReceiveState(item.lines))
    setExpandedMaterialId(null)
    setView('confirm')
  }

  const processScanPayload = useCallback(
    async (raw: string) => {
      const payload = raw.trim()
      if (!payload) {
        setToast(kitchenTerms.warehouseReceive.toast.scanTokenRequired)
        return
      }
      setIsVerifyingScan(true)
      setCameraActive(false)
      try {
        const res = await scanWarehouseDispatchQr(payload)
        setActiveDispatch({
          id: res.dispatch_id,
          code: res.dispatch_code,
          warehouseName: res.warehouse_name,
          delmanName: res.delman_name,
        })
        setReceiveLines(linesToReceiveState(res.lines))
        setScanToken('')
        setExpandedMaterialId(null)
        setView('confirm')
        await reload()
      } catch (e) {
        setToast(e instanceof Error ? e.message : kitchenTerms.warehouseReceive.toast.scanInvalid)
        setCameraActive(true)
      } finally {
        setIsVerifyingScan(false)
      }
    },
    [reload],
  )

  async function handleScanSubmit() {
    await processScanPayload(scanToken)
  }

  const handleCameraScan = useCallback(
    (value: string) => {
      if (isVerifyingScan) return
      setScanToken(value.trim())
      void processScanPayload(value)
    },
    [isVerifyingScan, processScanPayload],
  )

  function setLineGood(materialId: string, qtyGood: number) {
    setReceiveLines((prev) =>
      prev.map((line) => {
        if (line.materialId !== materialId) return line
        const nextGood = Math.max(0, Math.min(qtyGood, line.qtyPlanned))
        const nextDamaged = Math.min(line.qtyDamaged, line.qtyPlanned - nextGood)
        return { ...line, qtyGood: nextGood, qtyDamaged: nextDamaged }
      }),
    )
  }

  function setLineDamaged(materialId: string, qtyDamaged: number) {
    setReceiveLines((prev) =>
      prev.map((line) => {
        if (line.materialId !== materialId) return line
        const nextDamaged = Math.max(0, Math.min(qtyDamaged, line.qtyPlanned))
        const nextGood = Math.min(line.qtyGood, line.qtyPlanned - nextDamaged)
        return { ...line, qtyGood: nextGood, qtyDamaged: nextDamaged }
      }),
    )
  }

  function matchAllPlan() {
    setReceiveLines((prev) =>
      prev.map((line) => ({ ...line, qtyGood: line.qtyPlanned, qtyDamaged: 0 })),
    )
    setExpandedMaterialId(null)
  }

  function matchPlan(materialId: string) {
    setReceiveLines((prev) =>
      prev.map((line) =>
        line.materialId === materialId ? { ...line, qtyGood: line.qtyPlanned, qtyDamaged: 0 } : line,
      ),
    )
    setExpandedMaterialId((id) => (id === materialId ? null : id))
  }

  async function handleReceive(pin: string) {
    if (!activeDispatch) return
    try {
      await receiveWarehouseDispatch(
        activeDispatch.code,
        pin,
        receiveLines.map((line) => ({
          materialId: line.materialId,
          materialCode: line.materialName,
          qtyGood: line.qtyGood,
          qtyDamaged: line.qtyDamaged,
          qtyShortage: qtyShortageOf(line),
        })),
      )
      setToast(kitchenTerms.warehouseReceive.toast.received)
      setPinOpen(false)
      setView('list')
      setActiveDispatch(null)
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.warehouseReceive.toast.receiveFailed)
    }
  }

  if (!canPerformHeadActionsForSession) {
    return (
      <section className="space-y-4 pb-24">
        <div className="card space-y-3">
          <Link href="/kitchen/home" className="text-body-sm text-primary inline-flex items-center gap-1">
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            {kitchenTerms.warehouseReceive.backHome}
          </Link>
          <p className="text-headline-md text-primary break-words">{kitchenTerms.warehouseReceive.title}</p>
        </div>
        <EmptyState
          icon="lock"
          title={kitchenTerms.warehouseReceive.forbidden.title}
          description={kitchenTerms.warehouseReceive.forbidden.description}
        />
      </section>
    )
  }

  return (
    <section className="space-y-4 pb-24">
      <div className="card relative space-y-3">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          aria-label={kitchenTerms.warehouseReceive.refreshAria}
          title={kitchenTerms.warehouseReceive.refreshAria}
        >
          <span className="material-symbols-outlined">refresh</span>
        </button>

        <Link href="/kitchen/home" className="text-body-sm text-primary inline-flex items-center gap-1 pr-12">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          {kitchenTerms.warehouseReceive.backHome}
        </Link>
        <p className="text-headline-md text-primary break-words pr-12">{kitchenTerms.warehouseReceive.title}</p>
        <p className="text-body-sm text-on-surface-variant">{kitchenTerms.warehouseReceive.subtitle}</p>
        <p className="text-body-sm text-on-surface-variant capitalize">
          {kitchenName} · {todayLabel}
        </p>
        <div className="pt-3 border-t border-surface-variant">
          <FlowStepper steps={FLOW_STEPS} activeStep={flowStep(view)} />
        </div>
      </div>

      {loading && <SkeletonList count={3} label={kitchenTerms.warehouseReceive.loading} />}

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {kitchenTerms.warehouseReceive.retry}
          </button>
        </div>
      )}

      {!loading && !loadError && view === 'list' && (
        <div className="space-y-4">
          <button
            type="button"
            className="btn-primary w-full min-h-[44px] inline-flex items-center justify-center gap-2"
            onClick={() => {
              setScanToken('')
              setManualScanOpen(false)
              setView('scan')
            }}
          >
            <span className="material-symbols-outlined">qr_code_scanner</span>
            {kitchenTerms.warehouseReceive.list.scanCta}
          </button>

          {incoming.length === 0 ? (
            <EmptyState
              icon="move_to_inbox"
              title={kitchenTerms.warehouseReceive.list.emptyTitle}
              description={kitchenTerms.warehouseReceive.list.emptyDescription}
            />
          ) : (
            <>
              {pendingIncoming.length > 0 && (
                <div className="space-y-2">
                  <p className="text-body-sm font-semibold text-on-surface-variant">
                    {kitchenTerms.warehouseReceive.list.pendingSection}
                  </p>
                  {pendingIncoming.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="card w-full text-left hover:border-primary transition-colors"
                      onClick={() => {
                        if (item.receivable) openConfirmFromDispatch(item)
                        else setToast(kitchenTerms.warehouseReceive.toast.scanBeforeReceive)
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-body-lg font-semibold text-on-surface break-words">
                            {kitchenTerms.warehouseReceive.list.fromWarehouse(item.warehouse_name)}
                          </p>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {summaryText(item.lines)} · {item.delman_name}
                          </p>
                          <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                        </div>
                        <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                      </div>
                      {!item.receivable && (
                        <p className="text-body-sm text-status-warning mt-2">
                          {kitchenTerms.warehouseReceive.list.needsScan}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {doneIncoming.length > 0 && (
                <div className="space-y-2">
                  <p className="text-body-sm font-semibold text-on-surface-variant">
                    {kitchenTerms.warehouseReceive.list.doneSection}
                  </p>
                  {doneIncoming.map((item) => (
                    <div key={item.id} className="card">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-body-lg font-semibold text-on-surface break-words">
                            {kitchenTerms.warehouseReceive.list.fromWarehouse(item.warehouse_name)}
                          </p>
                          <p className="text-body-sm text-on-surface-variant mt-1">
                            {summaryText(item.lines)} · {item.delman_name}
                          </p>
                        </div>
                        <span className={statusBadge(item.status)}>{statusLabel(item.status)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {view === 'scan' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <div>
              <p className="text-headline-md text-primary">{kitchenTerms.warehouseReceive.scan.title}</p>
              <p className="text-body-sm text-on-surface-variant mt-1">
                {kitchenTerms.warehouseReceive.scan.description}
              </p>
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm font-semibold text-on-surface">Scan kamera</p>
              {cameraActive && !isVerifyingScan && (
                <span className="badge-warning text-label-md">{kitchenTerms.warehouseReceive.scan.liveBadge}</span>
              )}
            </div>

            <QrCameraScanner
              active={cameraActive && !isVerifyingScan}
              inactiveMessage={
                isVerifyingScan
                  ? kitchenTerms.warehouseReceive.scan.verifying
                  : kitchenTerms.warehouseReceive.scan.cameraHint
              }
              onScan={handleCameraScan}
              unsupportedMessage={kitchenTerms.warehouseReceive.scan.cameraUnsupported}
              permissionDeniedMessage={kitchenTerms.warehouseReceive.scan.cameraPermissionDenied}
              scanningHint={kitchenTerms.warehouseReceive.scan.cameraHint}
            />

            <details
              className="rounded-lg border border-outline-variant/60 bg-surface-container-low"
              open={manualScanOpen}
              onToggle={(e) => setManualScanOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer select-none px-3 py-3 text-body-sm font-medium min-h-[44px] flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-on-surface-variant">keyboard</span>
                {kitchenTerms.warehouseReceive.scan.manualToggle}
              </summary>
              <div className="space-y-3 border-t border-outline-variant/40 px-3 pb-3 pt-2">
                <label className="field-label" htmlFor="wd_scan_token">
                  {kitchenTerms.warehouseReceive.scan.tokenLabel}
                </label>
                <input
                  id="wd_scan_token"
                  className="input-field font-mono"
                  value={scanToken}
                  onChange={(e) => setScanToken(e.target.value.trim())}
                  placeholder={kitchenTerms.warehouseReceive.scan.tokenPlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={isVerifyingScan}
                />
                <button
                  type="button"
                  className="btn-primary w-full min-h-[48px] text-center disabled:opacity-40"
                  disabled={isVerifyingScan || !scanToken.trim()}
                  onClick={() => void handleScanSubmit()}
                >
                  {kitchenTerms.warehouseReceive.scan.verify}
                </button>
              </div>
            </details>
          </div>

          <button
            type="button"
            className="btn-outline w-full min-h-[44px]"
            onClick={() => setView('list')}
            disabled={isVerifyingScan}
          >
            {kitchenTerms.warehouseReceive.scan.cancel}
          </button>
        </div>
      )}

      {view === 'confirm' && activeDispatch && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary break-words">
              {kitchenTerms.warehouseReceive.confirm.title(activeDispatch.warehouseName)}
            </p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.warehouseReceive.confirm.meta(activeDispatch.code, activeDispatch.delmanName)}
            </p>
            <p className="text-body-sm text-on-surface-variant">{kitchenTerms.warehouseReceive.confirm.hint}</p>
          </div>

          <button
            type="button"
            className="btn-outline w-full min-h-[44px]"
            onClick={matchAllPlan}
          >
            {kitchenTerms.warehouseReceive.confirm.matchAllPlan}
          </button>

          <p className="text-body-sm font-semibold text-on-surface-variant">
            {kitchenTerms.warehouseReceive.confirm.summaryCounts(matchCounts.ok, matchCounts.diff)}
          </p>

          <div className="space-y-2">
            {receiveLines.map((line) => {
              const shortage = qtyShortageOf(line)
              const matched = isLineMatchPlan(line)
              const expanded = expandedMaterialId === line.materialId
              const panelId = `recv-panel-${line.materialId}`
              return (
                <div key={line.materialId} className="border border-outline-variant rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className="w-full px-3 py-2.5 bg-surface-container text-left min-h-[44px]"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() =>
                      setExpandedMaterialId((id) =>
                        id === line.materialId ? null : line.materialId,
                      )
                    }
                  >
                    <span className="flex items-start gap-2">
                      <span className="material-symbols-outlined text-lg text-on-surface-variant shrink-0 mt-0.5">
                        {expanded ? 'expand_less' : 'expand_more'}
                      </span>
                      <span className="flex-1 min-w-0 flex items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="block text-body-sm font-semibold text-on-surface break-words">
                            {line.materialName}
                          </span>
                          <span className="block text-body-sm text-on-surface-variant mt-0.5">
                            {kitchenTerms.warehouseReceive.confirm.planned(line.qtyPlanned, line.uom)}
                          </span>
                        </span>
                        <span
                          className={`shrink-0 ${matched ? 'badge-success' : 'badge-warning'}`}
                        >
                          {matched
                            ? kitchenTerms.warehouseReceive.confirm.statusOk
                            : kitchenTerms.warehouseReceive.confirm.statusDiff}
                        </span>
                      </span>
                    </span>
                  </button>

                  {expanded && (
                    <div id={panelId} className="border-t border-outline-variant p-3 space-y-3">
                      <div className="space-y-1">
                        <p className="text-body-sm font-medium text-on-surface">
                          {kitchenTerms.warehouseReceive.confirm.good}
                        </p>
                        <p className="text-label-md text-on-surface-variant">
                          {kitchenTerms.warehouseReceive.confirm.goodHint}
                        </p>
                        <QtyStepper
                          value={line.qtyGood}
                          min={0}
                          max={line.qtyPlanned - line.qtyDamaged}
                          onChange={(qtyGood) => setLineGood(line.materialId, qtyGood)}
                        />
                      </div>

                      <div className="space-y-1">
                        <p className="text-body-sm font-medium text-on-surface">
                          {kitchenTerms.warehouseReceive.confirm.damaged}
                        </p>
                        <p className="text-label-md text-on-surface-variant">
                          {kitchenTerms.warehouseReceive.confirm.damagedHint}
                        </p>
                        <QtyStepper
                          value={line.qtyDamaged}
                          min={0}
                          max={line.qtyPlanned - line.qtyGood}
                          onChange={(qtyDamaged) => setLineDamaged(line.materialId, qtyDamaged)}
                        />
                      </div>

                      <div className="rounded-lg bg-surface-container p-3 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-body-sm text-on-surface-variant">
                              {kitchenTerms.warehouseReceive.confirm.shortage}
                            </p>
                            <p className="text-label-md text-on-surface-variant">
                              {kitchenTerms.warehouseReceive.confirm.shortageHint}
                            </p>
                          </div>
                          <span className="text-body-lg font-bold text-on-surface shrink-0">
                            {shortage} {line.uom}
                          </span>
                        </div>
                        <p className="text-label-md text-on-surface-variant">
                          {kitchenTerms.warehouseReceive.confirm.equation(
                            line.qtyGood,
                            line.qtyDamaged,
                            shortage,
                            line.uom,
                          )}
                        </p>
                      </div>

                      <button
                        type="button"
                        className="text-body-sm text-primary font-semibold min-h-[44px]"
                        onClick={() => matchPlan(line.materialId)}
                      >
                        {kitchenTerms.warehouseReceive.confirm.matchPlan}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <StickyActionFooter>
            <button
              type="button"
              className="btn-outline flex-1 min-h-[44px]"
              onClick={() => {
                setView('list')
                setActiveDispatch(null)
              }}
            >
              {kitchenTerms.warehouseReceive.confirm.cancel}
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px] text-center disabled:opacity-40"
              disabled={!isBalanced}
              onClick={() => setPinOpen(true)}
            >
              {kitchenTerms.warehouseReceive.confirm.submit}
            </button>
          </StickyActionFooter>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={kitchenTerms.warehouseReceive.pin.title}
        description={kitchenTerms.warehouseReceive.pin.description}
        confirmLabel={kitchenTerms.warehouseReceive.pin.confirm}
        onClose={() => setPinOpen(false)}
        onConfirm={async (pin) => {
          await handleReceive(pin)
        }}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {!useApi && (
        <p className="text-caption text-on-surface-variant">{kitchenTerms.warehouseReceive.mockHint}</p>
      )}
    </section>
  )
}
